import {
  Injectable,
} from '@angular/core';
import {
  PaymentRecord,
  PaymentRecordsData,
  PaymentRecordsReply,
} from '@dapi/sdk';
import {
  CheckInPaymentRecordsStore,
  updateCheckInPaymentRecordsEntitiesFromApi,
} from '@des/store/check-in-payment-records';
import {
  JourneyUnpaidItemsStore,
  selectJourneyUnpaidItemsState,
  setJourneyUnpaidItems,
} from '@des/store/journey-unpaid-items';
import {
  JourneyModel,
  JourneysStore,
  patchJourneysEntity,
  selectSelectedJourney,
  setSelectedJourneyId,
} from '@des/store/journeys';
import {
  Actions,
  createEffect,
  ofType,
} from '@ngrx/effects';
import {
  select,
  Store,
} from '@ngrx/store';
import {
  AnalyticsSetPaymentData,
  AnalyticsSetProduct,
  AnalyticsSetTransactionPnr,
  selectAncillaryServicesProducts,
} from '@refx/analytics-core';
import {
  extractCategoriesAnalyticsFriendlyNames,
  extractServiceConfigurationMap,
  extractServicesAnalyticsFriendlyNames,
} from '@refx/shared-common';
import {
  ServicesConfigurationService,
} from '@refx/shared-services';
import {
  CATALOG_SERVICES_ACTION_TYPES,
  CatalogServicesStore,
  selectSelectedServices,
} from '@refx/shared-store';
import {
  selectSsciPaymentStatusState,
  SsciPaymentStatusStore,
} from '@refx/ssci-store';
import {
  combineLatest,
  from,
} from 'rxjs';
import {
  filter,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getTransactionData,
  getTransactionPnr,
} from './analytics.helpers';

@Injectable()
export class AnalyticsEffect {
  public populatePayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCheckInPaymentRecordsEntitiesFromApi),
      switchMap((action) =>
        from(action.call).pipe(
          filter(
            (reply): reply is PaymentRecordsReply & {data: PaymentRecordsData & {paymentRecords: PaymentRecord[];};} =>
              !!reply && !!reply.data && !!reply.data.paymentRecords && reply.data.paymentRecords.length > 0
          ),
          withLatestFrom(this.store.pipe(select(selectJourneyUnpaidItemsState)), this.store.pipe(select(selectSsciPaymentStatusState)), this.store.pipe(select(selectSelectedJourney))),
          switchMap(([reply, unpaidItems, paymentStatus, selectedJourney]) => {
            const {payment, products} = getTransactionData(selectedJourney, paymentStatus, unpaidItems, reply.data.paymentRecords[0].id);
            const pnr = getTransactionPnr(selectedJourney);
            return [
              AnalyticsSetPaymentData({
                payload: payment ? [payment] : []
              }),
              AnalyticsSetProduct({
                products,
                keepFlightTicket: true
              }),
              AnalyticsSetTransactionPnr(pnr)
            ];
          })
        )
      )
    )
  );

  public populateServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<any>(
        setSelectedJourneyId.type,
        patchJourneysEntity.type,
        setJourneyUnpaidItems.type,
        CATALOG_SERVICES_ACTION_TYPES.UPDATE
      ),
      switchMap(() => combineLatest([
        this.store.pipe(select(selectSelectedJourney), filter<JourneyModel>((selectedJourney) => !!selectedJourney), take(1)),
        this.store.pipe(select(selectJourneyUnpaidItemsState)),
        this.servicesConfigurationService.categoryDictionary$,
        this.servicesConfigurationService.serviceRichContents$,
        this.servicesConfigurationService.serviceDictionary$,
        this.store.pipe(select(selectSelectedServices))
      ])
        .pipe(
          withLatestFrom(this.store.pipe(select(selectAncillaryServicesProducts))),
          take(1),
          switchMap(([[journey, unpaidItems, categoryConfigurations, servicesRichContent, serviceDictionary, selectedServices], analyticsProducts]) => {
            const categoriesNames = extractCategoriesAnalyticsFriendlyNames(categoryConfigurations);
            const servicesNames = extractServicesAnalyticsFriendlyNames(servicesRichContent);
            const serviceCategoryMap = Object.entries(extractServiceConfigurationMap(serviceDictionary)).reduce(
              (acc: {[key: string]: string;}, [key, configuration]) => {
                acc[key] = configuration.categoryCode;
                return acc;
              },
              {}
            );
            const pnr = getTransactionPnr(journey);
            const {products} = getTransactionData(journey, undefined, unpaidItems, '', categoriesNames, servicesNames, serviceCategoryMap, selectedServices, analyticsProducts);
            return [
              AnalyticsSetProduct({
                products,
                keepFlightTicket: true
              }),
              AnalyticsSetTransactionPnr(pnr)
            ];
          })
        ))
    )
  );

  constructor(
    protected actions$: Actions,
    protected store: Store<CheckInPaymentRecordsStore & JourneysStore & JourneyUnpaidItemsStore & SsciPaymentStatusStore & CatalogServicesStore>,
    public servicesConfigurationService: ServicesConfigurationService
  ) {}
}
